import type { ReactNode } from "react";
import styles from "./IconButton.module.css";

interface IconButtonProps {
  children: ReactNode;
  /**
   * Visual variant:
   * - `outline` (default) — button-sm 32×32, bordered, action-default
   *   bg. Used for the day-strip chevrons.
   * - `ghost` — button-md 40×40, borderless, action-ghost-default
   *   (transparent) bg. Used inside the navbar back button.
   * - `elevated` — button-sm 32×32, borderless, action-elevated-default
   *   (#181818) bg. For actions sitting on a surface-raised parent
   *   like the search modal close button.
   *
   * All share the same hover (action-ghost-hover bg) and disabled
   * (foreground-disabled icon) behaviour.
   */
  variant?: "outline" | "ghost" | "elevated";
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  "aria-label"?: string | undefined;
  className?: string | undefined;
}

export function IconButton({
  children,
  variant = "outline",
  onClick,
  disabled,
  "aria-label": ariaLabel,
  className,
}: IconButtonProps) {
  return (
    <button
      type="button"
      className={[styles.iconButton, styles[variant], className]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}
